// Submissions: the per-form overview, the lists behind it, and one record.
//
// Round 28 reshaped this to ZT's: the page OPENS as one row per form —
// submitted count, unfinished count, last submitted — because ZT's flat list
// became unusable at ~50 templates and the overview was the fix. Drilling
// into a form (?templateId=) or asking for everything (?view=all) reaches the
// flat list, now with status tabs, a form filter, a date range and pages.
//
// The list is role-shaped without a role check in it: the server sends a field
// worker their own submissions and a manager everyone's. That is deliberate —
// a client-side filter is not an access control and must not look like one.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  formFill,
  type FormContent,
  type Submission,
  type SubmissionListItem,
} from '../formsApi';
import { ApiError } from '../api';
import { type MeterOption } from '../components/forms/FormFieldInput';
import { FormSections } from '../components/forms/FormSections';
import { MachineCard, machineFromSnapshot } from '../components/forms/MachineCard';
import { useSession } from '../session';

import {
  CardList,
  EmptyState,
  LinkedRow,
  PageHeader,
  RowCard,
  StatusBadge,
  useConfirm,
} from '../components/ui';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';
import { deviceTimeWorthStating, formatMomentAu } from '../lib/dates';
import { DownloadLink } from '../components/DownloadLink';
import { PdfFrame } from '../components/PdfViewer';

type OverviewSortKey = 'name' | 'submitted' | 'unfinished' | 'last';

const PER_PAGE = 50;

/**
 * Where a row goes. An unfinished one reopens where it was left off; a
 * completed one is a record and opens read-only.
 *
 * Shared by the card and the table halves deliberately — the pair only stays
 * in step if the things that could drift are written once.
 */
function rowHref(row: SubmissionListItem): string {
  return row.status === 'draft'
    ? `/app/forms/fill?draft=${row.id}`
    : `/app/forms/submissions/${row.id}`;
}

function whoAndWhen(row: SubmissionListItem): string {
  // A draft names who STARTED it: a manager's list shows the whole company's
  // unfinished forms, and "Started 12/09" without a name is a question
  // nobody can act on.
  return row.submittedAt
    ? `${row.submittedByName ?? 'Someone'} · ${new Date(row.submittedAt).toLocaleDateString('en-AU')}`
    : `${row.startedByName ?? 'Someone'} · started ${new Date(row.createdAt).toLocaleDateString('en-AU')}`;
}

function SubmissionBadge({ status }: { status: string }) {
  return (
    <StatusBadge
      kind="expiry"
      status={status === 'submitted' ? 'ok' : 'soon'}
      label={status === 'submitted' ? 'Completed' : 'Not finished'}
    />
  );
}

interface OverviewRow {
  templateId: string;
  templateName: string;
  submittedCount: number;
  draftCount: number;
  lastSubmittedAt: string | null;
}

/** The status tabs. "Unfinished", never "draft", anywhere a person reads. */
const TABS = [
  { key: '', label: 'All' },
  { key: 'draft', label: 'Unfinished' },
  { key: 'submitted', label: 'Submitted' },
] as const;

export function SubmissionListPage() {
  const [params] = useSearchParams();
  const templateId = params.get('templateId') ?? '';
  const flatView = params.get('view') === 'all';
  const showOverview = !flatView && templateId === '';

  return showOverview ? <SubmissionsOverview /> : <SubmissionsFiltered />;
}

/** One row per form, and the way into each form's own list. */
function SubmissionsOverview() {
  const [rows, setRows] = useState<OverviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { sortKey, sortDir, onSort } = useSort<OverviewSortKey>('name');

  useEffect(() => {
    void formFill.overview().then(
      (result) => setRows(result.forms),
      (err) => setError(err instanceof ApiError ? err.message : 'Could not load those forms.')
    );
  }, []);

  const visible = useMemo(
    () =>
      sortRows(
        rows ?? [],
        (row) => {
          switch (sortKey) {
            case 'name':
              return row.templateName;
            // Counts, as numbers — 2 before 10, which a text sort reverses.
            case 'submitted':
              return row.submittedCount;
            case 'unfinished':
              return row.draftCount;
            case 'last':
              return row.lastSubmittedAt ? new Date(row.lastSubmittedAt) : null;
          }
        },
        sortDir
      ),
    [rows, sortKey, sortDir]
  );

  const counts = (row: OverviewRow) =>
    [
      `${row.submittedCount} submitted`,
      row.draftCount > 0 ? `${row.draftCount} unfinished` : null,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <>
      <PageHeader
        title="Submissions"
        subtitle="Forms already filled in, and any left unfinished."
        actions={
          <Link to="/app/forms" className="btn-primary btn-sm">
            Fill in a form
          </Link>
        }
      />

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      {rows && rows.length === 0 ? (
        <EmptyState
          title="Nothing filled in yet"
          body="Completed forms show up here, with the photos and the signature exactly as they were signed."
          action={
            <Link to="/app/forms" className="btn-primary btn-sm">
              Fill in a form
            </Link>
          }
        />
      ) : rows ? (
        <>
          <CardList>
            {visible.map((row) => (
              <RowCard
                key={row.templateId}
                to={`/app/forms/submissions?templateId=${row.templateId}`}
                title={row.templateName}
              >
                <div>{counts(row)}</div>
                {row.lastSubmittedAt && (
                  <div>Last {new Date(row.lastSubmittedAt).toLocaleDateString('en-AU')}</div>
                )}
              </RowCard>
            ))}
          </CardList>
          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Form" sortKey="name" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Submitted" sortKey="submitted" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Unfinished" sortKey="unfinished" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Last submitted" sortKey="last" current={sortKey} dir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <LinkedRow
                    key={row.templateId}
                    to={`/app/forms/submissions?templateId=${row.templateId}`}
                  >
                    <td className="font-medium">
                      <Link
                        to={`/app/forms/submissions?templateId=${row.templateId}`}
                        className="table-link"
                      >
                        {row.templateName}
                      </Link>
                    </td>
                    <td className="table-muted">{row.submittedCount}</td>
                    <td className="table-muted">{row.draftCount === 0 ? '' : row.draftCount}</td>
                    <td className="table-muted">
                      {row.lastSubmittedAt
                        ? new Date(row.lastSubmittedAt).toLocaleDateString('en-AU')
                        : ''}
                    </td>
                  </LinkedRow>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm">
            <Link to="/app/forms/submissions?view=all" className="table-link">
              View every submission in one list
            </Link>
          </p>
        </>
      ) : (
        !error && <p className="muted">Loading…</p>
      )}
    </>
  );
}

/** The flat list: one form's submissions, or everything, filtered and paged. */
function SubmissionsFiltered() {
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const templateId = params.get('templateId') ?? '';
  const status = params.get('status') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  const [rows, setRows] = useState<SubmissionListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [templateName, setTemplateName] = useState<string | null>(null);
  const [formOptions, setFormOptions] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await formFill.list({
        templateId: templateId || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        perPage: PER_PAGE,
      });
      setRows(result.submissions);
      setTotal(result.total);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load those forms.');
    }
  }, [templateId, status, from, to, page]);

  useEffect(() => {
    void load();
  }, [load]);

  // The drill-down's heading, and the flat view's form filter, both come off
  // the overview rollup — one request that already exists.
  useEffect(() => {
    void formFill.overview().then(
      (result) => {
        setFormOptions(result.forms.map((f) => ({ id: f.templateId, name: f.templateName })));
        if (templateId) {
          setTemplateName(
            result.forms.find((f) => f.templateId === templateId)?.templateName ?? null
          );
        }
      },
      () => undefined
    );
  }, [templateId]);

  /** Change one filter; every filter change goes back to page 1. */
  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setParams(next);
  }

  async function deleteDraft(row: SubmissionListItem) {
    const sure = await confirm({
      title: 'Delete this unfinished form?',
      message: 'Everything typed and every photo on it goes too. This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!sure) return;
    try {
      await formFill.discard(row.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that form.');
    }
  }

  const rangeApplied = from !== '' || to !== '';
  const first = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const last = Math.min(page * PER_PAGE, total);

  const actionsFor = (row: SubmissionListItem) =>
    row.status === 'draft' ? (
      <button type="button" className="table-link text-red-700" onClick={() => void deleteDraft(row)}>
        Delete
      </button>
    ) : (
      // A plain anchor, not the in-app viewer: `?download=1` answers with an
      // attachment under a server-built filename a person can file.
      <DownloadLink href={formFill.pdfDownloadUrl(row.id)} className="table-link">
        Download PDF
      </DownloadLink>
    );

  return (
    <>
      <PageHeader
        title={templateName ?? 'Submissions'}
        subtitle={
          templateId
            ? 'Everything filled in against this form.'
            : 'Every submission in one list.'
        }
        actions={
          <Link to="/app/forms" className="btn-primary btn-sm">
            Fill in a form
          </Link>
        }
      />

      <p className="mb-4 text-sm">
        <Link to="/app/forms/submissions" className="table-link">
          &larr; All forms
        </Link>
      </p>

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      {/* The status tabs. */}
      <div className="mb-4 flex flex-wrap items-center gap-2" role="tablist" aria-label="Status">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={status === tab.key}
            className={status === tab.key ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
            onClick={() => setFilter('status', tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters: the form (flat view only — the drill-down IS the form
          filter), and a submitted-date range. */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        {!templateId && formOptions.length > 0 && (
          <div>
            <label className="form-label" htmlFor="filter-form">
              Form
            </label>
            <select
              id="filter-form"
              className="form-select"
              value=""
              onChange={(e) => {
                if (e.target.value) setFilter('templateId', e.target.value);
              }}
            >
              <option value="">All forms</option>
              {formOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="form-label" htmlFor="filter-from">
            From
          </label>
          <input
            id="filter-from"
            type="date"
            className="form-input"
            value={from}
            onChange={(e) => setFilter('from', e.target.value)}
          />
        </div>
        <div>
          <label className="form-label" htmlFor="filter-to">
            To
          </label>
          <input
            id="filter-to"
            type="date"
            className="form-input"
            value={to}
            onChange={(e) => setFilter('to', e.target.value)}
          />
        </div>
        {(rangeApplied || status !== '') && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => {
              const next = new URLSearchParams(params);
              for (const key of ['status', 'from', 'to', 'page']) next.delete(key);
              setParams(next);
            }}
          >
            Clear
          </button>
        )}
      </div>

      {rows && rows.length === 0 ? (
        <EmptyState
          title="Nothing here"
          body={
            rangeApplied && status === 'draft'
              ? 'Unfinished forms have no submitted date, so a date range never matches them.'
              : rangeApplied
                ? 'Nothing was submitted in that date range.'
                : 'Completed forms show up here, with the photos and the signature exactly as they were signed.'
          }
          action={
            <Link to="/app/forms" className="btn-primary btn-sm">
              Fill in a form
            </Link>
          }
        />
      ) : rows ? (
        <>
          <p className="mb-2 text-sm text-gray-600">
            {first === 1 && last === total
              ? `${total} form${total === 1 ? '' : 's'}`
              : `${first}–${last} of ${total}`}
          </p>
          <CardList>
            {rows.map((row) => (
              <RowCard
                key={row.id}
                to={rowHref(row)}
                title={row.templateName}
                badges={<SubmissionBadge status={row.status} />}
                actions={actionsFor(row)}
              >
                <div>
                  {/* The snapshot, so a deleted job still reads properly. */}
                  {row.jobNumber ?? (row.kind === 'asset' ? 'Machine' : 'No job')}
                </div>
                <div>{whoAndWhen(row)}</div>
              </RowCard>
            ))}
          </CardList>
          {/* The desktop half of the standard card/table pair.
              THIS PAGE HAD ONLY THE CardList UNTIL 2026-09-02, and CardList is
              `sm:hidden` — so on any screen at or above 640px the Forms list
              showed a heading and nothing else, and no completed form could be
              opened at all. Both halves are covered by
              `assertRendersInCardAndTable` now. */}
          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Form</th>
                  <th>Status</th>
                  <th>Job or machine</th>
                  <th>Filled in by</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <LinkedRow key={row.id} to={rowHref(row)}>
                    <td className="font-medium">
                      <Link to={rowHref(row)} className="table-link">
                        {row.templateName}
                      </Link>
                    </td>
                    <td>
                      <SubmissionBadge status={row.status} />
                    </td>
                    <td className="table-muted">
                      {row.jobNumber ?? (row.kind === 'asset' ? 'Machine' : 'No job')}
                    </td>
                    <td className="table-muted">{whoAndWhen(row)}</td>
                    <td className="text-right">{actionsFor(row)}</td>
                  </LinkedRow>
                ))}
              </tbody>
            </table>
          </div>

          {total > PER_PAGE && (
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setParams((p) => {
                  const next = new URLSearchParams(p);
                  next.set('page', String(page - 1));
                  return next;
                })}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={last >= total}
                onClick={() => setParams((p) => {
                  const next = new URLSearchParams(p);
                  next.set('page', String(page + 1));
                  return next;
                })}
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        !error && <p className="muted">Loading…</p>
      )}
    </>
  );
}

export function SubmissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useSession();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [content, setContent] = useState<FormContent | null>(null);
  const [meters, setMeters] = useState<MeterOption[]>([]);
  const [jobTitle, setJobTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPdf, setShowPdf] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const result = await formFill.get(id);
      setSubmission(result.submission);
      setContent(result.content);
      // From the submission, not the fleet API: that one is manager-only, and
      // a field worker filling in a prestart has to see the meter.
      setMeters(result.meters);
      setJobTitle(result.jobTitle);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that form.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!submission || !content) {
    return error ? (
      <div className="banner-error" role="alert">
        {error}
      </div>
    ) : (
      <p className="muted">Loading…</p>
    );
  }

  return (
    <>
      <PageHeader
        title={content.title || 'Form'}
        subtitle={
          submission.submittedAt
            ? // Filled in with no signal and sent later (round 42): both
              // times, each saying whose clock it is, as the PDF states them.
              deviceTimeWorthStating(submission.deviceSubmittedAt, submission.submittedAt)
              ? `${submission.submittedByName ?? 'Someone'} · completed on the phone ${formatMomentAu(
                  submission.deviceSubmittedAt!
                )} · received ${formatMomentAu(submission.submittedAt)}`
              : `${submission.submittedByName ?? 'Someone'} · ${new Date(
                  submission.submittedAt
                ).toLocaleString('en-AU')}`
            : undefined
        }
        actions={
          <>
            <DownloadLink href={formFill.pdfDownloadUrl(submission.id)} className="btn-secondary btn-sm">
              Download PDF
            </DownloadLink>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setShowPdf((open) => !open)}
            >
              {showPdf ? 'Hide the PDF' : 'View the PDF'}
            </button>
          </>
        }
      />

      <p className="mb-4 text-sm">
        <Link to="/app/forms/submissions" className="table-link">
          &larr; Submissions
        </Link>
      </p>

      {/* What the form was about. The NUMBER is the snapshot; the title
          resolves live, and only a manager gets the link — a field worker's
          job pages answer 404, and a link that bounces to My schedule is
          worse than plain text. */}
      {submission.jobNumber && (
        <p className="mb-4 text-sm text-gray-700">
          About{' '}
          {submission.jobId && (user?.role === 'admin' || user?.role === 'manager') ? (
            <Link className="table-link" to={`/app/jobs/${submission.jobId}`}>
              {submission.jobNumber}
              {jobTitle ? ` · ${jobTitle}` : ''}
            </Link>
          ) : (
            <span className="font-medium text-gray-900">
              {submission.jobNumber}
              {jobTitle ? ` · ${jobTitle}` : ''}
            </span>
          )}
        </p>
      )}

      {/* The machine AS THE RECORD STATES IT — its own block, from the
          snapshot, deliberately separate from any header that would show
          today's name. After a rename the two differ, and both are correct
          (round 28). */}
      {submission.resourceSnapshot && (
        <div className="mb-6 max-w-2xl">
          <MachineCard machine={machineFromSnapshot(submission.resourceSnapshot)} />
        </div>
      )}

      {showPdf && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-2">
          <PdfFrame
            src={formFill.pdfUrl(submission.id)}
            downloadHref={formFill.pdfDownloadUrl(submission.id)}
            label="The completed form"
          />
        </div>
      )}

      <div className="max-w-2xl">
        {/* The SAME components the fill screen uses, read-only. Two
            renderers would drift, and the read-back has to show exactly
            what was shown — including which options were not ticked. */}
        <FormSections
          content={content}
          answers={submission.answers}
          files={submission.files}
          meters={meters}
          submissionId={submission.id}
          readOnly
          signedCaption={
            submission.submittedAt
              ? `Signed · ${submission.submittedByName ?? 'Someone'} · ${new Date(
                  // Signed on the phone, so the phone's day when it has one.
                  deviceTimeWorthStating(submission.deviceSubmittedAt, submission.submittedAt)
                    ? submission.deviceSubmittedAt!
                    : submission.submittedAt
                ).toLocaleDateString('en-AU')}`
              : undefined
          }
        />
      </div>
    </>
  );
}
